import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const defaultValue = {
  pendingCount: 0,
  isLoading: false,
  bootstrapped: false,
  markBootstrapped: () => {},
  start: () => null,
  stop: () => {},
  track: async (promiseOrFn) => {
    if (typeof promiseOrFn === 'function') return promiseOrFn();
    return promiseOrFn;
  }
};

const LoadingContext = createContext(defaultValue);

export const useLoading = () => {
  return useContext(LoadingContext);
};

export const LoadingProvider = ({ children }) => {
  const [pendingCount, setPendingCount] = useState(0);
  const [bootstrapped, setBootstrapped] = useState(false);
  const tokensRef = useRef(new Set());

  const markBootstrapped = useCallback(() => {
    setBootstrapped(true);
  }, []);

  const start = useCallback(() => {
    const token = Symbol('loading');
    tokensRef.current.add(token);
    setPendingCount((c) => c + 1);
    return token;
  }, []);

  const stop = useCallback((token) => {
    if (!token) return;
    if (!tokensRef.current.has(token)) return;

    tokensRef.current.delete(token);
    setPendingCount((c) => Math.max(0, c - 1));
  }, []);

  const track = useCallback(
    async (promiseOrFn) => {
      const token = start();
      try {
        if (typeof promiseOrFn === 'function') {
          return await promiseOrFn();
        }
        return await promiseOrFn;
      } finally {
        stop(token);
      }
    },
    [start, stop]
  );

  const value = useMemo(
    () => ({
      pendingCount,
      isLoading: pendingCount > 0,
      bootstrapped,
      markBootstrapped,
      start,
      stop,
      track
    }),
    [pendingCount, bootstrapped, markBootstrapped, start, stop, track]
  );

  return <LoadingContext.Provider value={value}>{children}</LoadingContext.Provider>;
};

export default LoadingContext;
